import { DurationPicker } from '@salecycle/ui-component-library';
import * as React from 'react';

export enum Interval {
  'Months' = 'Months',
  'Weeks' = 'Weeks',
  'Days' = 'Days',
  'Hours' = 'Hours',
}

export interface Props {
  duration: number;
  onChange: (duration: number) => void;
}

interface Values {
  howMany: number;
  interval: Interval;
}

const options = Object.keys(Interval).map(interval => ({
  value: interval,
  label: interval,
}));

const anHour = 60 * 60 * 1000;
const aDay = 24 * anHour;
const aWeek = 7 * aDay;
const aMonth = 4 * aWeek;

const durations: { [key in Interval]: number } = {
  Months: aMonth,
  Weeks: aWeek,
  Days: aDay,
  Hours: anHour,
};

const getValuesFromInterval = (timeInMillis: number, interval: Interval) => ({
  howMany: timeInMillis / durations[interval],
  interval,
});

const getIfDivisible = (timeInMillis: number, interval: Interval): Values | null =>
  timeInMillis % durations[interval] === 0 ? getValuesFromInterval(timeInMillis, interval) : null;

export const ReshowSelector: React.FC<Props> = props => {
  const [interval, setInterval] = React.useState(null);

  const getValues = (timeInMillis: number): Values => {
    if (interval) {
      return getValuesFromInterval(timeInMillis, interval);
    }
    const values = Object.keys(Interval)
      .map(x => getIfDivisible(timeInMillis, x as Interval))
      .filter(x => x !== null);
    return values[0] ? values[0] : getValuesFromInterval(timeInMillis, Interval.Months);
  };

  const values = getValues(props.duration);
  const isValidDuration = (duration: number) => duration > 0;

  const handleDurationChange = (value: number, unit: string) => {
    const duration = isValidDuration(value) ? value : 1;

    setInterval(unit);

    props.onChange(duration * durations[unit]);
  };

  return (
    <DurationPicker
      value={values.howMany}
      onChange={handleDurationChange}
      label="Show again every"
      unit={values.interval}
      ariaLabel="Value"
      unitAriaLabel="Unit"
      units={options}
    />
  );
};
